import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Brand from "../../api/Brand";
import Region from "../../api/Region";
import AddressBook from "../../api/AddressBook";
import Company from "../../api/Company";
import Location from "../../api/Location";
import { toast } from "react-hot-toast";
import { emitCustomEvent, useCustomEventListener } from "react-custom-events";
import Input from "../../components/form/Input";
import Select from "../../components/form/Select";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { DashboardLayout } from "../../components/organisms/Layout";
import Category from "../../api/Category";
import PlacesAutocomplete from "../../components/form/PlacesAutocomplete";
import { decodeIds } from "../../utils";
import AddNewAddressModal from "../../components/modals/AddNewAddressModal";

const AddLocation = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const editMode = location.pathname.search("edit") !== -1;
  const [name, setName] = useState("");
  const [brands, setBrands] = useState([]);
  const [brand, setBrand] = useState();
  const [regions, setRegions] = useState([]);
  const [region, setRegion] = useState();
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState();
  const [addresses, setAddresses] = useState([]);
  const [address, setAddress] = useState(null);
  const [newAddress, setNewAddress] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [company, setCompany] = useState("");
  const [website, setWebsite] = useState("");
  const [urlSlug, setUrlSlug] = useState("");
  const [validationErrors, setValidationErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddAddressModal, setShowAddAddressModal] = useState(false);
  const decodedId = decodeIds(params?.id)[0];

  const loadBrands = () => {
    Brand.getAll().then((response) => {
      setBrands(response.data.data);
      if (!editMode)
        setBrand(
          response.data.data.length > 0 ? response.data.data[0].id : null
        );
    });
  };

  const loadRegions = () => {
    Region.getAll().then((response) => {
      setRegions(response.data.data);
      if (!editMode) {
        setRegion(
          response.data.data.length > 0 ? response.data.data[0].id : null
        );
      }
    });
  };

  const loadAddresses = (init = true) => {
    AddressBook.getAll().then((response) => {
      const data = Array.isArray(response?.data?.data) ? response.data.data : [];
      setAddresses(data);
      if (init && !editMode && company) {
        const companyAddresses = data.filter((item) => item.company_id === company);
        setAddress(companyAddresses[0]?.id ?? null);
      }
    });
  };

  const loadCategories = () => {
    Category.getAll().then((response) => {
      setCategories(response.data.data);
      if (!editMode) {
        setCategory(
          response.data.data.length > 0
            ? [parseInt(response.data.data[0].id)]
            : null
        );
      }
    });
  };

  const loadCompanies = () => {
    Company.getAll().then((response) => {
      setCompanies(response.data.data);
      if (!editMode) {
        setCompany(
          response.data.data.length > 0 ? response.data.data[0].id : null
        );
      }
    });
  };

  const addLocation = () => {
    setLoading(true);
    let _address = null;
    if (newAddress) {
      _address = newAddress;
      _address.company_id = company;
      setNewAddress(_address);
    }
    Location.add({
      name: name,
      website: website,
      company_id: company,
      address_id: address,
      brand_id: brand,
      region_id: region,
      category_id: category,
      url_slug: urlSlug,
    })
      .then(() => {
        toast.success(t("locations.location-added"));
        emitCustomEvent("dataChanged");
        emitCustomEvent("closeModal");
        navigate("/companies/locations");
        if (newAddress) {
          saveNewAddress(_address);
        }
      })
      .catch((error) => {
        if (error.response && error.response.status === 422) {
          setValidationErrors(error.response.data.errors);
        } else if (error.response && error.response.status === 500) {
          toast.success(t("locations.location-added"));
          emitCustomEvent("dataChanged");
          emitCustomEvent("closeModal");
          navigate("/companies/locations");
        } else {
          if (error?.response?.status !== 403) {
            toast.error(t("common.something-went-wrong-please-try-again"));
          }
        }
      })
      .then(() => setLoading(false));
  };

  const filteredBrands = () => {
    if (!company) return [];
    return brands.filter((brand) => brand.company_id === company);
  };

  const filteredRegions = () => {
    if (!company) return [];
    return regions.filter((region) => region.company_id === company);
  };

  const filteredAddresses = () => {
    if (!company) return [];
    return addresses.filter(
      (item) =>
        item?.id != null &&
        item?.name &&
        String(item.name).trim() !== "" &&
        item.company_id === company
    );
  };

  const addressOptions = useMemo(
    () =>
      filteredAddresses().map((item) => ({
        value: item.id,
        label: String(item.name),
      })),
    [addresses, company]
  );

  const selectedAddressValue = useMemo(() => {
    if (address == null || address === "") {
      return null;
    }
    const hasMatch = addressOptions.some(
      (opt) => opt.value === address || String(opt.value) === String(address)
    );
    return hasMatch ? address : null;
  }, [address, addressOptions]);

  const categoryOptions = useMemo(
    () =>
      categories.map((item) => ({
        value: item.id,
        label: String(item.name),
      })),
    [categories]
  );

  const selectedCategoryValue = useMemo(() => {
    if (category == null || category === "") {
      return null;
    }
    const categoryId = Array.isArray(category) ? category[0] : category;
    if (categoryId == null || categoryId === "") {
      return null;
    }
    const hasMatch = categoryOptions.some(
      (opt) =>
        opt.value === categoryId ||
        String(opt.value) === String(categoryId)
    );
    return hasMatch ? categoryId : null;
  }, [category, categoryOptions]);

  const filterCompanies = () => {
    const _arrangeCompanies = companies.sort(
      (a, b) => b.is_enterprise - a.is_enterprise
    );
    return _arrangeCompanies;
  };

  const loadLocation = () => {
    setLoading(true);
    Location.getById(decodedId)
      .then((response) => {
        const location = response.data.data;
        setName(location?.name);
        setCompany(location?.company?.id);
        setBrand(location?.brand?.id);
        setRegion(location?.region?.id);
        setCategory(location?.category_id);
        setAddress(location?.address?.id);
        setWebsite(location?.website);
        setUrlSlug(location?.url_slug);
      })
      .catch((error) => {
        if (error?.response?.status !== 403) {
          toast.error(t("common.something-went-wrong"));
        }
      })
      .finally(() => setLoading(false));
  };

  const updateLocation = () => {
    setLoading(true);
    let _address = null;
    if (newAddress) {
      _address = newAddress;
      _address.company_id = company;
      setNewAddress(_address);
    }
    
    Location.update(decodedId, {
      name: name,
      website: website,
      company_id: company,
      address_id: address,
      brand_id: brand,
      region_id: region,
      category_id: category,
      url_slug: urlSlug,
    })
      .then(() => {
        toast.success(t("locations.location-updated"));
        emitCustomEvent("dataChanged");
        emitCustomEvent("closeModal");
        navigate("/companies/locations");
        if (newAddress) {
          saveNewAddress(_address);
        }
      })
      .catch((error) => {
        if (error.response && error.response.status === 422) {
          setValidationErrors(error.response.data.errors);
        } else {
          if (error?.response?.status !== 403) {
            toast.error(t("common.something-went-wrong-please-try-again"));
          }
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (editMode) {
      document.title = "HeyHire Portal-Edit-Location";
    } else {
      document.title = "HeyHire Portal-Add-New-Location";
    }
    loadBrands();
    loadRegions();
    loadAddresses();
    loadCompanies();
    loadCategories();
    if (editMode) {
      loadLocation();
    }
  }, []);

  useEffect(() => {
    if (!company || addresses.length === 0) return;

    const availableAddresses = filteredAddresses();

    if (editMode) {
      const availableBrands = filteredBrands();
      const availableRegions = filteredRegions();

      // Only update if the current value is not in the available options
      if (brand && !availableBrands.some(b => b.id === brand)) {
        setBrand(availableBrands.length > 0 ? availableBrands[0].id : null);
      }
      
      if (region && !availableRegions.some(r => r.id === region)) {
        setRegion(availableRegions.length > 0 ? availableRegions[0].id : null);
      }

      if (address && !availableAddresses.some(a => a.id === address)) {
        setAddress(availableAddresses.length > 0 ? availableAddresses[0].id : null);
      }
    } else {
      // In add mode, set default values
      setAddress(filteredAddresses().length > 0 ? filteredAddresses()[0].id : null);
      setBrand(filteredBrands().length > 0 ? filteredBrands()[0].id : null);
      setRegion(filteredRegions().length > 0 ? filteredRegions()[0].id : null);
    }
  }, [company, addresses]);

  const handleAddressAdded = (newAddress) => {
    loadAddresses(false);
    setAddress(newAddress.id);
  };

  useCustomEventListener("placeSelected", (data) => {
    const vName = data?.terms[0]?.value;
    const param = {
      name: vName,
      address: data?.addressLine,
      city: data?.terms[data.terms.length - 3]?.value,
      state: data?.terms[data.terms.length - 2]?.value,
      zip: data?.zipCode,
      lat: data?.lat,
      lng: data?.lng,
      company_id: company,
    };
    // set name
    setName(vName);
    // save new address
    setNewAddress(param);
  });

  const saveNewAddress = async (param) => {
    // check if address exists
    try {
      const exists = addresses.filter((a) => a.name === param.name);
      if (exists.length < 1) {
        const resp = await AddressBook.add(param);
        loadAddresses(true);
        setAddress(resp.data.data.id);
      } else {
        setAddress(exists[0].id);
      }
    } catch (error) {
      toast.error("failed to save address");
    }
  };

  return (
    <DashboardLayout darmo={true}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3">
            <Link
              to="/companies/locations"
              className="text-gray-500 hover:text-gray-700"
            >
              <i className="far fa-chevron-left text-xl" />
            </Link>
            <h2 className="text-2xl font-bold text-gray-900">
              {editMode
                ? t("locations.edit-location")
                : t("locations.add-a-location")}
            </h2>
          </div>
          <p className="mt-1 text-sm text-gray-600">
            Fill in the information below to {editMode ? "update" : "create"} a
            location.
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-white shadow-sm ring-1 ring-gray-300 rounded-xl p-6">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (editMode) {
                updateLocation();
              } else {
                addLocation();
              }
            }}
            className="space-y-8"
          >
            {/* Location Information Section */}
            <div>
              <h3 className="text-base font-semibold leading-7 text-gray-900">
                Location Information
              </h3>
              <p className="mt-1 text-sm leading-6 text-gray-600">
                Basic details about the location.
              </p>

              <div className="mt-6 grid grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-2">
                <div className="col-span-full sm:col-span-1">
                  <Select
                    name="company"
                    label={t("locations.assign-a-business")}
                    placeholder={t("locations.assign-a-business")}
                    value={company}
                    error={
                      validationErrors && validationErrors.company_id
                        ? validationErrors.company_id[0]
                        : null
                    }
                    onChange={(e) => setCompany(parseInt(e.target.value))}
                  >
                    {filterCompanies().map((company) => (
                      <option key={company?.id} value={company?.id}>
                        {company.name}
                      </option>
                    ))}
                  </Select>
                </div>

                <div className="col-span-full sm:col-span-1">
                  <PlacesAutocomplete
                    darmo={true}
                    label={t("common.name")}
                    placeholder={t("common.name")}
                    pValue={name}
                    isAddressSearch={false}
                    error={
                      validationErrors && validationErrors.name
                        ? validationErrors.name[0]
                        : null
                    }
                  />
                </div>

                <div className="col-span-full sm:col-span-1">
                  <Select
                    name="address"
                    label={t("locations.assign-address")}
                    placeholder={t("common.please-select-one")}
                    options={addressOptions}
                    value={selectedAddressValue}
                    error={
                      validationErrors && validationErrors.address_id
                        ? validationErrors.address_id[0]
                        : null
                    }
                    onChange={(e) => {
                      const nextValue = e.target.value;
                      setAddress(
                        nextValue === "" || nextValue == null
                          ? null
                          : Number(nextValue)
                      );
                    }}
                    disabled={!company}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (!company) {
                        toast.error(t("locations.assign-a-business"));
                        return;
                      }
                      setShowAddAddressModal(true);
                    }}
                    className="mt-2 text-sm font-semibold text-primary-600 hover:text-primary-800"
                  >
                    + {t("address-book.add-address")}
                  </button>
                </div>

                <div className="col-span-full sm:col-span-1">
                  <Select
                    name="brand"
                    label={t("locations.assign-a-brand")}
                    value={brand || ""}
                    placeholder={t("common.please-select-one")}
                    error={
                      validationErrors && validationErrors.brand_id
                        ? validationErrors.brand_id[0]
                        : null
                    }
                    onChange={(e) => setBrand(parseInt(e.target.value))}
                    disabled={!company || filteredBrands().length === 0}
                  >
                    {filteredBrands().length > 0 ? (
                      filteredBrands().map((brand) => (
                        <option key={brand.id} value={brand.id}>
                          {brand.name}
                        </option>
                      ))
                    ) : (
                      <option value="">No brands available</option>
                    )}
                  </Select>
                </div>

                <div className="col-span-full sm:col-span-1">
                  <Select
                    name="category"
                    label={t("locations.business-category")}
                    placeholder={t("common.please-select-one")}
                    options={categoryOptions}
                    value={selectedCategoryValue}
                    error={
                      validationErrors && validationErrors.category_id
                        ? validationErrors.category_id[0]
                        : null
                    }
                    onChange={(e) => {
                      const nextValue = e.target.value;
                      setCategory(
                        nextValue === "" || nextValue == null
                          ? null
                          : [Number(nextValue)]
                      );
                    }}
                  />
                </div>

                <div className="col-span-full sm:col-span-1">
                  <Input
                    darmo={true}
                    name="website"
                    label={t("common.website")}
                    type="text"
                    value={website}
                    slug="https://"
                    placeholder={t("common.website")}
                    error={
                      validationErrors && validationErrors.website
                        ? validationErrors.website[0]
                        : null
                    }
                    onChange={(e) => setWebsite(e.target.value)}
                  />
                </div>

                <div className="col-span-full sm:col-span-1">
                  <Input
                    darmo={true}
                    name="url_slug"
                    label={t("common.url-slug")}
                    type="text"
                    value={urlSlug}
                    placeholder={t("common.url-slug")}
                    error={
                      validationErrors && validationErrors.urlSlug
                        ? validationErrors.urlSlug[0]
                        : null
                    }
                    onChange={(e) => setUrlSlug(e.target.value)}
                  />
                </div>

                <div className="col-span-full sm:col-span-1">
                  <Select
                    name="region"
                    label={t("locations.assign-a-region")}
                    value={region}
                    placeholder={t("common.please-select-one")}
                    error={
                      validationErrors && validationErrors.region_id
                        ? validationErrors.region_id[0]
                        : null
                    }
                    onChange={(e) => setRegion(parseInt(e.target.value))}
                  >
                    {filteredRegions().map((region) => (
                      <option key={region.id} value={region.id}>
                        {region.name}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="border-t border-gray-900/10 pt-8 flex justify-end gap-x-3">
              <button
                type="button"
                onClick={() => navigate("/companies/locations")}
                className="rounded-lg px-4 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="rounded-lg bg-primary-500 px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[100px]"
              >
                {loading ? (
                  <i className="fad fa-circle-notch fa-spin" />
                ) : (
                  t("common.save")
                )}
              </button>
            </div>
          </form>
        </div>

        <AddNewAddressModal
          open={showAddAddressModal}
          setOpen={setShowAddAddressModal}
          companyId={company}
          onSuccess={handleAddressAdded}
        />
      </div>
    </DashboardLayout>
  );
};

export default AddLocation;
